import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
import { sha256Schema } from "./opaque.js";
const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/iu);
export const narrativeLiveArtDirectionSchema = z
    .object({
    contract: z.literal("narrative-live-art-direction/1"),
    product: narrativeBookProductSchema,
    sourceBound: z.literal(true),
    styleAnchor: z
        .object({
        medium: z.string().trim().min(20).max(500),
        palette: z.array(hexColorSchema).min(4).max(7),
        paperAndMaterialLanguage: z.string().trim().min(20).max(500),
        compositionalRhythm: z.string().trim().min(20).max(500),
        prohibitedTraits: z.array(z.string().trim().min(5).max(240)).min(5).max(15),
    })
        .strict(),
    assets: z
        .array(z
        .object({
        assetId: z.string().trim().min(1).max(200),
        chapterId: z.string().trim().min(1).max(200),
        sourceBoundSubject: z.string().trim().min(10).max(500),
        composition: z.enum([
            "QUIET_STILL_LIFE",
            "OVERHEAD_OBJECT_STUDY",
            "ARCHITECTURAL_FRAGMENT",
            "ROUTE_OR_DISTANCE_MOTIF",
            "DOMESTIC_SPACE_STUDY",
        ]),
        lighting: z.enum([
            "SOFT_WINDOW_DAYLIGHT",
            "OVERCAST_NATURAL_LIGHT",
            "WARM_LATE_AFTERNOON",
            "COOL_EARLY_MORNING",
            "RESTRAINED_STUDIO_LIGHT",
        ]),
        palette: z.array(hexColorSchema).min(3).max(6),
        materialTreatment: z.string().trim().min(15).max(400),
        emotionalFunction: z.string().trim().min(15).max(400),
        avoid: z.array(z.string().trim().min(3).max(200)).min(5).max(15),
    })
        .strict())
        .length(5),
})
    .strict();
const scoreSchema = z.number().int().min(1).max(5);
export const narrativeLiveVisualReviewSchema = z
    .object({
    contract: z.literal("narrative-live-visual-review/1"),
    product: narrativeBookProductSchema,
    assets: z
        .array(z
        .object({
        assetId: z.string().trim().min(1).max(200),
        sourceFidelity: scoreSchema,
        editorialQuality: scoreSchema,
        productFit: scoreSchema,
        sequenceDistinctness: scoreSchema,
        documentaryHonesty: scoreSchema,
        pass: z.boolean(),
        blockingDefects: z.array(z.string().trim().min(3).max(500)).max(8),
        repairInstruction: z.string().trim().min(10).max(1_000).nullable(),
    })
        .strict())
        .length(5),
    sequence: z
        .object({
        paletteProgression: scoreSchema,
        compositionalVariety: scoreSchema,
        bookSpecificity: scoreSchema,
        avoidsPictureBookLanguage: z.boolean(),
        avoidsFakeDocumentaryLanguage: z.boolean(),
        pass: z.boolean(),
        blockingDefects: z.array(z.string().trim().min(3).max(500)).max(10),
    })
        .strict(),
    pass: z.boolean(),
})
    .strict();
export const narrativePaidProductionManifestSchema = z
    .object({
    contract: z.literal("narrative-paid-production-manifest/1"),
    product: narrativeBookProductSchema,
    syntheticContent: z.literal(true),
    proseMode: z.literal("FIXTURE_SOURCE_BOUND_SONNET_REVIEWED"),
    visualMode: z.literal("PAID_MODEL"),
    textModelId: z.string().trim().min(1).max(200),
    imageModelId: z.string().trim().min(1).max(200),
    providerCallCount: z.number().int().positive(),
    paidProviderCallCount: z.number().int().positive(),
    textProviderCallCount: z.number().int().positive(),
    imageProviderCallCount: z.number().int().min(6),
    actualCostMicros: z.number().int().nonnegative(),
    budgetLimitMicros: z.number().int().positive(),
    artDirectionChecksumSha256: sha256Schema,
    visualReviewChecksumSha256: sha256Schema,
    manuscriptChecksumSha256: sha256Schema,
    pdfChecksumSha256: sha256Schema,
    pageCount: z.number().int().positive(),
    wordCount: z.number().int().positive(),
    assetChecksums: z
        .array(z
        .object({
        assetId: z.string().trim().min(1).max(200),
        checksumSha256: sha256Schema,
        truthClass: z.literal("EDITORIAL_ILLUSTRATION"),
        ownerReviewRequired: z.literal(true),
    })
        .strict())
        .length(5),
    validation: z
        .object({
        privacy: z.literal("PASS"),
        provenance: z.literal("PASS"),
        manuscriptUnchanged: z.literal("PASS"),
        composition: z.literal("PASS"),
        typography: z.literal("PASS"),
        geometry: z.literal("PASS"),
        imageSequence: z.enum([
            "PASS",
            "PASS_WITH_OWNER_REVIEW_REQUIRED",
            "BLOCKED_OWNER_REVIEW_REQUIRED",
        ]),
    })
        .strict(),
    ownerReviewRequired: z.literal(true),
    productionReady: z.literal(false),
    blockers: z.array(z.string().trim().min(1).max(1_000)).min(1).max(20),
    builtAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=narrative-live-production.js.map